import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Eye, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminPages() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(30);
  const [search, setSearch] = useState("");
  const [editPage, setEditPage] = useState(null);
  const [viewPage, setViewPage] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/pages", { params: { search, skip: 0, limit } });
    setRows(data.items); setTotal(data.total);
  }, [search, limit]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (id) => {
    const { data } = await api.get(`/admin/pages/${id}`);
    setEditPage(data);
  };
  const openView = async (id) => {
    const { data } = await api.get(`/admin/pages/${id}`);
    setViewPage(data);
  };
  const savePage = async () => {
    await api.put(`/admin/pages/${editPage.id}`, { page_name: editPage.page_name, fields: editPage.fields });
    toast.success("Saved");
    setEditPage(null); load();
  };
  const regen = async () => {
    try {
      const { data } = await api.post(`/admin/pages/${editPage.id}/generate`);
      setEditPage({ ...editPage, fields: data.fields });
      toast.success("New content generated");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Regeneration failed";
      toast.error(msg);
    }
  };
  const addPage = async () => {
    await api.post("/admin/pages", { page_name: `Page ${total + 1}`, fields: [] });
    toast.success("Page added");
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Typing Pages</h1>
          <p className="text-zinc-500 mt-2 text-sm">Total: {total}</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input data-testid="pages-search" placeholder="Search by page name…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2.5 border border-[var(--line)] rounded-full text-sm bg-white" />
          </div>
          <select data-testid="pages-limit" value={limit} onChange={(e) => setLimit(+e.target.value)} className="border border-[var(--line)] rounded-full px-3 py-2.5 text-sm bg-white">
            {[20, 30, 50, 100, 200].map((n) => <option key={n}>{n}</option>)}
          </select>
          <button data-testid="add-page-btn" onClick={addPage} className="btn-primary"><Plus size={16} /> Add Page</button>
        </div>
      </div>

      <div className="card-clean overflow-x-auto">
        <table className="w-full table-clean">
          <thead className="bg-zinc-50">
            <tr>{["#", "Page Name", "Edit", "View"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--line)] hover:bg-zinc-50">
                <td className="px-4 py-3 font-mono text-xs">{r.page_number}</td>
                <td className="px-4 py-3 font-medium">{r.page_name}</td>
                <td className="px-4 py-3"><button data-testid={`page-edit-${r.page_number}`} onClick={() => openEdit(r.id)} className="p-1.5 rounded hover:bg-zinc-100"><Pencil size={14} /></button></td>
                <td className="px-4 py-3"><button data-testid={`page-view-${r.page_number}`} onClick={() => openView(r.id)} className="p-1.5 rounded hover:bg-zinc-100"><Eye size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {limit < total && (
        <div className="text-center mt-6">
          <button data-testid="load-more-pages" onClick={() => setLimit(limit + 30)} className="btn-ghost">Load More</button>
        </div>
      )}

      {editPage && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditPage(null)}>
          <div className="card-clean p-8 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-2">Edit {editPage.page_name}</h3>
            <p className="text-zinc-500 text-sm mb-4">9 fields per page. Edit individually or regenerate all.</p>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
              {editPage.fields.map((f, i) => (
                <div key={`${editPage.id}-edit-${i}`}>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Field {i + 1}</label>
                  <textarea rows={2} value={f} onChange={(e) => {
                    const nf = [...editPage.fields]; nf[i] = e.target.value;
                    setEditPage({ ...editPage, fields: nf });
                  }} className="mt-1 w-full border border-[var(--line)] rounded-lg px-3 py-2.5 font-mono text-sm" />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6 justify-between">
              <button data-testid="generate-all-btn" onClick={regen} className="btn-ghost"><RefreshCw size={14} /> Generate All</button>
              <div className="flex gap-2">
                <button onClick={() => setEditPage(null)} className="btn-ghost">Cancel</button>
                <button data-testid="page-save-btn" onClick={savePage} className="btn-primary">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewPage && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setViewPage(null)}>
          <div className="card-clean p-8 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-4">{viewPage.page_name}</h3>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {viewPage.fields.map((f, i) => (
                <div key={`${viewPage.id}-view-${i}`} className="bg-zinc-50 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Field {i + 1}</div>
                  <div className="font-mono text-sm leading-7">{f}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setViewPage(null)} className="btn-ghost">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
